import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { organisationService } from '../../services/serviceFactory';
import { useAuth } from '../../contexts/AuthContext';
import { User } from '../../services/interfaces';
import { SelectChangeEvent } from '@mui/material';

// Email validation regex
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
// Simple phone validation regex - allows various formats but requires minimum digits
const phoneRegex = /^[+\s\d()-]{7,20}$/;

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user: authUser } = useAuth(); 
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<Partial<User>>({
    name: '',
    email: '',
    phone: '',
    isDeleted: 0
  });
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
  }>({});
  const [userStatusFilter, setUserStatusFilter] = useState<string>('active');
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, userStatusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await organisationService.getUsers();
      setUsers(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError(t('users.errorLoadingUsers'));
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    if (userStatusFilter === 'all') {
      setFilteredUsers(users);
    } else if (userStatusFilter === 'active') {
      setFilteredUsers(users.filter(user => user.isDeleted === 0));
    } else if (userStatusFilter === 'inactive') {
      setFilteredUsers(users.filter(user => user.isDeleted === 1));
    }
  };

  const handleUserStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setUserStatusFilter(event.target.value);
  };

  const validateForm = (): boolean => {
    const errors: {
      name?: string;
      email?: string;
      phone?: string;
    } = {};

    // Validate required fields
    if (!formData.name?.trim()) {
      errors.name = t('users.nameRequired');
    }

    if (!formData.email?.trim()) {
      errors.email = t('users.emailRequired');
    } else if (!emailRegex.test(formData.email)) {
      errors.email = t('users.invalidEmail');
    }

    // Phone is optional but should be valid if provided
    if (formData.phone?.trim() && !phoneRegex.test(formData.phone)) {
      errors.phone = t('users.invalidPhone');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 0 : 1) : value
    });

    // Clear validation error when field is modified
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined
      });
    }
  };

  const handleOpenDialog = (user: User | null = null) => {
    // Reset form errors
    setFormErrors({});

    if (user) {
      setCurrentUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        isDeleted: user.isDeleted
      });
    } else {
      setCurrentUser(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        isDeleted: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentUser(null);
  };

  const handleOpenDeleteDialog = (user: User) => {
    setCurrentUser(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setCurrentUser(null);
  };

  const toggleUserStatus = async (user: User) => {
    try {
      setLoading(true);
      const updatedUser = {
        ...user,
        isDeleted: user.isDeleted === 0 ? 1 : 0
      };
      
      await organisationService.updateUser(updatedUser);
      await fetchUsers();
    } catch (err) {
      console.error('Failed to update user status:', err);
      setError(t('users.errorUpdatingUser'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      if (currentUser) {
        // Update existing user
        await organisationService.updateUser({
            ...currentUser,
            ...formData,
            isDeleted: formData.isDeleted ?? 0, // Ensure not undefined
            organisationId: currentUser.organisationId
          } as User);
      } else {
        // Create new user
        if (authUser?.organisationId) {
          const organisationId = authUser?.organisationId;
          await organisationService.addUser({
            ...formData,
            organisationId
          });
        }
      }
      
      handleCloseDialog();
      await fetchUsers();
    } catch (err) {
      console.error('Failed to save user:', err);
      setError(currentUser ? t('users.errorUpdatingUser') : t('users.errorAddingUser'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      await organisationService.deleteUser(currentUser.id);
      handleCloseDeleteDialog();
      await fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError(t('users.errorDeletingUser'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('organisation.userManagement')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilter(!showFilter)}
          >
            {t('filter')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {t('users.addUser')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showFilter && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControl fullWidth>
            <InputLabel>{t('users.filterByStatus')}</InputLabel>
            <Select
              value={userStatusFilter}
              label={t('users.filterByStatus')}
              onChange={handleUserStatusFilterChange}
            >
              <MenuItem value="all">{t('users.status.all')}</MenuItem>
              <MenuItem value="active">{t('users.status.active')}</MenuItem>
              <MenuItem value="inactive">{t('users.status.inactive')}</MenuItem>
            </Select>
          </FormControl>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('users.name')}</TableCell>
              <TableCell>{t('users.email')}</TableCell>
              <TableCell>{t('users.phone')}</TableCell>
              <TableCell>{t('users.status.root')}</TableCell>
              <TableCell>{t('users.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers?.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    {user.name}
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.phone || '-'}</TableCell>
                <TableCell>
                  {user.isDeleted === 0 ? (
                    <Chip 
                      label={t('users.active')} 
                      color="success" 
                      size="small" 
                      icon={<CheckCircleIcon />}
                    />
                  ) : (
                    <Chip 
                      label={t('users.inactive')} 
                      color="error" 
                      size="small" 
                      icon={<BlockIcon />}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex' }}>
                    <Tooltip title={t('users.edit')}>
                      <IconButton onClick={() => handleOpenDialog(user)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={user.isDeleted === 0 ? t('users.deactivate') : t('users.activate')}>
                      <IconButton 
                        onClick={() => toggleUserStatus(user)}
                        color={user.isDeleted === 0 ? "error" : "success"}
                      >
                        {user.isDeleted === 0 ? <BlockIcon /> : <CheckCircleIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {(!filteredUsers || filteredUsers.length === 0) && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {t('users.noUsers')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentUser ? t('users.editUser') : t('users.addUser')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label={t('users.name')}
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="email"
                label={t('users.email')}
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                required
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="phone"
                label={t('users.phone')}
                value={formData.phone}
                onChange={handleInputChange}
                fullWidth
                error={!!formErrors.phone}
                helperText={formErrors.phone}
              />
            </Grid>
            {currentUser && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="isActive"
                      checked={formData.isDeleted === 0}
                      onChange={handleInputChange}
                      color="primary"
                    />
                  }
                  label={t('users.active')}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!formData.name || !formData.email}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{t('users.deleteUserTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('users.deleteUserConfirm', { name: currentUser?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagement;